/* ==============================================================
   TERI FARRARI — CORE BEHAVIOUR
   --------------------------------------------------------------
   Shared runtime for the whole theme. No dependencies, no build
   step, ~5KB. Loaded once with `defer` from layout/theme.liquid.

   Provides:
     • TF.reveal        scroll reveal with stagger (all sections)
     • TF.headerHeight  keeps --tf-header-height accurate
     • TF.trapFocus     shared focus trap for drawers/modals
     • TF.busy          shared loading-state helper
     • <tf-sticky-header>, <tf-disclosure>, <tf-drawer>,
       <tf-rotator>, <tf-video-facade>, <tf-carousel>   custom elements

   Everything is idempotent and re-initialises on
   `shopify:section:load`, so the theme editor stays live.
   ============================================================== */
(function () {
  'use strict';

  if (window.TF) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------
     Reveal on scroll
     ------------------------------------------------------------ */
  var revealObserver = null;

  function revealSetup() {
    if (revealObserver || !('IntersectionObserver' in window)) return;
    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          revealObserver.unobserve(el);
          el.setAttribute('data-tf-revealed', '');
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -6% 0px' }
    );
  }

  function reveal(root) {
    root = root || document;
    if (reduceMotion.matches || !('IntersectionObserver' in window)) return;
    revealSetup();

    var nodes = root.querySelectorAll('[data-tf-reveal]:not([data-tf-reveal-ready])');
    if (!nodes.length) return;

    // Stagger is computed per parent so a row of cards cascades, but
    // two separate rows both start from zero delay.
    var seen = new Map();

    Array.prototype.forEach.call(nodes, function (el) {
      var parent = el.parentNode;
      var index = seen.get(parent) || 0;
      seen.set(parent, index + 1);

      var stagger = parseInt(el.getAttribute('data-tf-reveal-stagger'), 10);
      if (isNaN(stagger)) {
        stagger =
          parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--tf-reveal-stagger'),
            10
          ) || 80;
      }

      el.style.setProperty('--tf-reveal-delay', Math.min(index, 8) * stagger + 'ms');
      el.setAttribute('data-tf-reveal-ready', '');
      revealObserver.observe(el);
    });
  }

  /* ------------------------------------------------------------
     Header height -> --tf-header-height
     Used for sticky offsets, drawer top, and scroll-margin so that
     in-page anchors never land beneath the header.
     ------------------------------------------------------------ */
  var headerRO = null;

  function measureHeader() {
    var header = document.querySelector('[data-tf-header]');
    var height = header ? Math.round(header.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--tf-header-height', height + 'px');
    // Announcement bar height -> --tf-topbar-h on each header section (lets the bar scroll away while the header stays sticky).
    document.querySelectorAll('[data-tf-header]').forEach(function (h) { var tb = h.querySelector(':scope > .tf-topbar'); h.parentElement.style.setProperty('--tf-topbar-h', (tb ? Math.round(tb.getBoundingClientRect().height) : 0) + 'px'); });
  }

  function watchHeader() {
    measureHeader();
    if (!('ResizeObserver' in window)) {
      window.addEventListener('resize', measureHeader, { passive: true });
      return;
    }
    if (headerRO) headerRO.disconnect();
    headerRO = new ResizeObserver(measureHeader);
    var header = document.querySelector('[data-tf-header]');
    document.querySelectorAll('[data-tf-header]').forEach(function (h) { headerRO.observe(h); });
  }

  /* ------------------------------------------------------------
     Focus trap (drawers, modals)
     ------------------------------------------------------------ */
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, initial) {
    var nodes = Array.prototype.filter.call(
      container.querySelectorAll(FOCUSABLE),
      function (el) {
        return el.offsetParent !== null;
      }
    );
    if (!nodes.length) return function () {};

    var first = nodes[0];
    var last = nodes[nodes.length - 1];

    function onKey(event) {
      if (event.key !== 'Tab') return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKey);
    (initial || first).focus({ preventScroll: true });

    return function release() {
      container.removeEventListener('keydown', onKey);
    };
  }

  /* ------------------------------------------------------------
     Loading-state helper — one API for every async interaction
     ------------------------------------------------------------ */
  function busy(el, state) {
    if (!el) return;
    if (el.tagName === 'BUTTON' || el.classList.contains('tf-btn')) {
      el.toggleAttribute('data-loading', state);
      el.setAttribute('aria-busy', state ? 'true' : 'false');
      if (state) {
        el.setAttribute('aria-disabled', 'true');
      } else {
        el.removeAttribute('aria-disabled');
      }
    } else {
      el.setAttribute('data-tf-busy', state ? 'true' : 'false');
      el.setAttribute('aria-busy', state ? 'true' : 'false');
    }
  }

  /* Any form marked data-tf-loading-form shows the shared spinner on
     its submit button the moment it is submitted. */
  function bindForms(root) {
    (root || document)
      .querySelectorAll('form[data-tf-loading-form]:not([data-tf-form-bound])')
      .forEach(function (form) {
        form.setAttribute('data-tf-form-bound', '');
        form.addEventListener('submit', function () {
          if (!form.checkValidity()) return;
          busy(form.querySelector('[type="submit"]'), true);
        });
      });
  }

  /* ------------------------------------------------------------
     Customer-form success messages

     Shopify reports `form.posted_successfully?` on EVERY customer form
     on the page, not just the one that was submitted — so signing up in
     the footer would also show "your extract is on its way" halfway up
     the page. The redirect carries the submitted form's id as the URL
     fragment, so anything that does not match it is hidden.

     Deliberately fail-safe: with no fragment, nothing is hidden and the
     behaviour is exactly Shopify's own.
     ------------------------------------------------------------ */
  function scopeFormSuccess() {
    var messages = document.querySelectorAll('[data-tf-form-success]');
    if (messages.length < 2) return;

    var submitted = (window.location.hash || '').replace('#', '');
    if (!submitted) return;

    Array.prototype.forEach.call(messages, function (el) {
      if (el.getAttribute('data-tf-form-success') !== submitted) {
        el.hidden = true;
      }
    });
  }

  /* ------------------------------------------------------------
     Scroll lock (shared counter so nested drawers behave)
     ------------------------------------------------------------ */
  var lockCount = 0;
  var lockScrollY = 0;

  function lockScroll(state) {
    if (state) {
      if (lockCount === 0) {
        lockScrollY = window.scrollY;
        document.body.style.top = -lockScrollY + 'px';
        document.body.classList.add('tf-no-scroll');
      }
      lockCount += 1;
    } else {
      lockCount = Math.max(0, lockCount - 1);
      if (lockCount === 0) {
        document.body.classList.remove('tf-no-scroll');
        document.body.style.top = '';
        window.scrollTo(0, lockScrollY);
      }
    }
  }

  /* ------------------------------------------------------------
     <tf-sticky-header>
     Adds data-state="pinned|unpinned|top" for CSS to react to.
     ------------------------------------------------------------ */
  class TFStickyHeader extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.stickyType || 'always';
      this.lastY = window.scrollY;
      this.ticking = false;
      this.threshold = parseInt(this.dataset.threshold, 10) || 60;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    }

    update() {
      var y = window.scrollY;
      var scrolled = y > this.threshold;

      this.toggleAttribute('data-scrolled', scrolled);

      if (this.mode === 'on-scroll-up') {
        // Never hide while a menu inside the header is open.
        var menuOpen = this.querySelector('[aria-expanded="true"]');
        if (!scrolled || menuOpen) {
          this.dataset.state = 'top';
        } else if (y > this.lastY + 4) {
          this.dataset.state = 'unpinned';
        } else if (y < this.lastY - 4) {
          this.dataset.state = 'pinned';
        }
      } else {
        this.dataset.state = scrolled ? 'pinned' : 'top';
      }

      this.lastY = y;
    }
  }

  /* ------------------------------------------------------------
     <tf-disclosure> — accessible dropdown (nav submenus)
     Opens on click; on desktop also on hover when data-hover is set.
     ------------------------------------------------------------ */
  class TFDisclosure extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-tf-disclosure-trigger]');
      this.panel = this.querySelector('[data-tf-disclosure-panel]');
      if (!this.trigger || !this.panel) return;

      this.hoverEnabled = this.hasAttribute('data-hover');
      this.closeTimer = null;

      // A footer column is an accordion on a phone and plain, always
      // open content on desktop. Above the breakpoint the trigger stops
      // being a control, and says so to assistive tech.
      var accMax = parseInt(this.dataset.accordionMax, 10);
      if (!isNaN(accMax)) {
        this.accMQ = window.matchMedia('(min-width: ' + (accMax + 1) + 'px)');
        this.syncAccordion = () => {
          if (this.accMQ.matches) {
            this.setAttribute('data-static', '');
            this.trigger.setAttribute('aria-expanded', 'true');
            this.trigger.setAttribute('aria-disabled', 'true');
          } else {
            this.removeAttribute('data-static');
            this.trigger.removeAttribute('aria-disabled');
            this.trigger.setAttribute(
              'aria-expanded',
              this.hasAttribute('data-open') ? 'true' : 'false'
            );
          }
        };
        this.syncAccordion();
        this.accMQ.addEventListener('change', this.syncAccordion);
      }

      this.trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggle(!this.isOpen);
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen) {
          this.toggle(false);
          this.trigger.focus();
        }
      });

      // Close when focus or pointer leaves the whole disclosure.
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.toggle(false);
      });

      if (this.hoverEnabled) {
        this.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'touch') return;
          if (!window.matchMedia('(min-width: 990px)').matches) return;
          clearTimeout(this.closeTimer);
          this.toggle(true);
        });
        this.addEventListener('pointerleave', (event) => {
          if (event.pointerType === 'touch') return;
          clearTimeout(this.closeTimer);
          this.closeTimer = setTimeout(() => this.toggle(false), 180);
        });
      }

      this.onDocClick = (event) => {
        if (this.isOpen && !this.contains(event.target)) this.toggle(false);
      };
      document.addEventListener('click', this.onDocClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      clearTimeout(this.closeTimer);
      if (this.accMQ) this.accMQ.removeEventListener('change', this.syncAccordion);
    }

    get isOpen() {
      return this.trigger.getAttribute('aria-expanded') === 'true';
    }

    toggle(open) {
      // Above its breakpoint an accordion is static content, not a
      // control, so clicks and outside-click closes are ignored.
      if (this.hasAttribute('data-static')) return;
      if (open === this.isOpen) return;
      this.trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.toggleAttribute('data-open', open);

      if (open) {
        // Only one dropdown open at a time within the same menu bar.
        var root = this.closest('[data-tf-menu]') || document;
        root.querySelectorAll('tf-disclosure[data-open]').forEach((other) => {
          if (other !== this) other.toggle(false);
        });
      }
    }
  }

  /* ------------------------------------------------------------
     <tf-drawer> — mobile navigation / off-canvas panel
     ------------------------------------------------------------ */
  class TFDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-tf-drawer-panel]');
      this.overlay = this.querySelector('[data-tf-drawer-overlay]');
      this.releaseFocus = null;
      this.openerEl = null;

      this.querySelectorAll('[data-tf-drawer-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });

      var id = this.id;
      if (id) {
        document.querySelectorAll('[data-tf-drawer-open="' + id + '"]').forEach((btn) => {
          btn.addEventListener('click', (event) => {
            event.preventDefault();
            this.open(btn);
          });
        });
      }

      // A drawer must never stay open once the layout goes desktop.
      this.mq = window.matchMedia('(min-width: 990px)');
      this.onMQ = () => {
        if (this.mq.matches) this.close();
      };
      this.mq.addEventListener('change', this.onMQ);
    }

    disconnectedCallback() {
      if (this.mq) this.mq.removeEventListener('change', this.onMQ);
      if (this.hasAttribute('data-open')) lockScroll(false);
    }

    open(opener) {
      if (this.hasAttribute('data-open')) return;
      this.openerEl = opener || null;
      this.setAttribute('data-open', '');
      this.setAttribute('aria-hidden', 'false');
      if (opener) opener.setAttribute('aria-expanded', 'true');
      lockScroll(true);
      this.releaseFocus = trapFocus(this.panel || this);
    }

    close() {
      if (!this.hasAttribute('data-open')) return;
      this.removeAttribute('data-open');
      this.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      if (this.releaseFocus) this.releaseFocus();
      this.releaseFocus = null;
      if (this.openerEl) {
        this.openerEl.setAttribute('aria-expanded', 'false');
        this.openerEl.focus({ preventScroll: true });
        this.openerEl = null;
      }
    }
  }

  /* ------------------------------------------------------------
     <tf-rotator> — cross-fading announcements
     ------------------------------------------------------------ */
  class TFRotator extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-tf-rotator-item]'));
      if (this.items.length < 2) return;

      this.index = 0;
      this.interval = (parseFloat(this.dataset.speed) || 5) * 1000;

      if (reduceMotion.matches) return;

      this.start();

      // Pause while hovered, focused, or the tab is hidden.
      this.addEventListener('pointerenter', () => this.stop());
      this.addEventListener('pointerleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
      this.onVisibility = () => (document.hidden ? this.stop() : this.start());
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    start() {
      this.stop();
      this.timer = setInterval(() => this.next(), this.interval);
    }

    stop() {
      clearInterval(this.timer);
    }

    next() {
      this.items[this.index].removeAttribute('data-active');
      this.index = (this.index + 1) % this.items.length;
      this.items[this.index].setAttribute('data-active', '');
    }
  }

  /* ------------------------------------------------------------
     <tf-video-facade>
     Holds the player in a <template> so the iframe is not in the DOM
     — and therefore makes no request — until someone actually clicks.
     An embedded YouTube player costs roughly a megabyte of script on
     load, which is the heaviest thing a marketing page can do to its
     Core Web Vitals for content most visitors never play.
     ------------------------------------------------------------ */
  class TFVideoFacade extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-tf-video-play]');
      this.template = this.querySelector('template');
      if (!this.trigger || !this.template) return;

      this.onActivate = this.onActivate.bind(this);
      this.trigger.addEventListener('click', this.onActivate);

      // Warm the connection on intent, so the click feels immediate.
      this.addEventListener('pointerenter', () => this.preconnect(), { once: true });
      this.trigger.addEventListener('focus', () => this.preconnect(), { once: true });
    }

    preconnect() {
      var host = this.dataset.origin;
      if (!host || this.warmed) return;
      this.warmed = true;
      ['preconnect', 'dns-prefetch'].forEach(function (rel) {
        var link = document.createElement('link');
        link.rel = rel;
        link.href = host;
        link.crossOrigin = '';
        document.head.appendChild(link);
      });
    }

    onActivate(event) {
      event.preventDefault();
      if (this.loaded) return;
      this.loaded = true;
      this.setAttribute('data-loading', '');

      var player = this.template.content.firstElementChild.cloneNode(true);
      var parent = this.parentNode;

      // An <iframe> reports readiness with 'load', a <video> with
      // 'loadeddata'. Listen for both, and stop spinning regardless
      // after a moment so a blocked third party cannot leave the
      // spinner turning forever.
      var settle = () => this.removeAttribute('data-loading');
      player.addEventListener('load', settle, { once: true });
      player.addEventListener('loadeddata', settle, { once: true });
      setTimeout(settle, 4000);

      parent.appendChild(player);
      this.remove();

      // A <video> can start playing straight away; an <iframe> is told
      // to autoplay by its own src.
      if (player.tagName === 'VIDEO' && typeof player.play === 'function') {
        var attempt = player.play();
        if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
      }

      if (typeof player.focus === 'function') player.focus({ preventScroll: true });
    }
  }

  /* ------------------------------------------------------------
     <tf-qty> — quantity stepper
     ------------------------------------------------------------
     A number input with two buttons. The input stays a real number
     input, so a keyboard and a screen reader get the native control and
     the buttons are only a convenience on top of it.
     ------------------------------------------------------------ */
  function TFQty() {
    return Reflect.construct(HTMLElement, [], TFQty);
  }
  TFQty.prototype = Object.create(HTMLElement.prototype);
  TFQty.prototype.constructor = TFQty;
  Object.setPrototypeOf(TFQty, HTMLElement);

  TFQty.prototype.connectedCallback = function () {
    var el = this;
    this.input = this.querySelector('input');
    if (!this.input) return;

    this.addEventListener('click', function (event) {
      var button = event.target.closest('[data-step]');
      if (!button) return;
      event.preventDefault();

      var step = parseInt(button.getAttribute('data-step'), 10) || 0;
      var min = parseInt(el.input.min, 10);
      if (isNaN(min)) min = 1;
      var max = parseInt(el.input.max, 10);

      var next = (parseInt(el.input.value, 10) || min) + step;
      if (next < min) next = min;
      if (!isNaN(max) && next > max) next = max;

      el.input.value = next;
      el.input.dispatchEvent(new Event('change', { bubbles: true }));
      el.refresh();
    });

    this.input.addEventListener('change', function () {
      el.refresh();
    });
    this.refresh();
  };

  TFQty.prototype.refresh = function () {
    var min = parseInt(this.input.min, 10);
    if (isNaN(min)) min = 1;
    var max = parseInt(this.input.max, 10);
    var value = parseInt(this.input.value, 10) || min;

    var down = this.querySelector('[data-step="-1"]');
    var up = this.querySelector('[data-step="1"]');
    if (down) down.disabled = value <= min;
    if (up) up.disabled = !isNaN(max) && value >= max;
  };

  /* ------------------------------------------------------------
     <tf-variants> — variant picker
     ------------------------------------------------------------
     Dawn's own variant-selects re-fetches the whole section over the
     network on every change, which needs Dawn's product-info markup and
     drags its stylesheets in with it. These products have two or three
     variants, so the whole variant list is already on the page: pick the
     match locally and update the few things that differ.

     Reads the variant list from a <script type="application/json">,
     which is how Shopify recommends passing structured data to the
     front end without it being parsed as markup.
     ------------------------------------------------------------ */
  function TFVariants() {
    return Reflect.construct(HTMLElement, [], TFVariants);
  }
  TFVariants.prototype = Object.create(HTMLElement.prototype);
  TFVariants.prototype.constructor = TFVariants;
  Object.setPrototypeOf(TFVariants, HTMLElement);

  TFVariants.prototype.connectedCallback = function () {
    var el = this;
    var data = this.querySelector('script[type="application/json"]');
    if (!data) return;

    try {
      this.variants = JSON.parse(data.textContent);
    } catch (error) {
      return;
    }

    this.form = this.closest('form');
    this.root = this.closest('[data-tf-product]') || document;

    this.addEventListener('change', function () {
      el.update();
    });
  };

  TFVariants.prototype.selected = function () {
    return Array.prototype.map.call(
      this.querySelectorAll('select, input:checked'),
      function (field) {
        return field.value;
      }
    );
  };

  TFVariants.prototype.update = function () {
    var chosen = this.selected();
    var match = null;

    for (var i = 0; i < this.variants.length; i++) {
      var variant = this.variants[i];
      var same = true;
      for (var n = 0; n < chosen.length; n++) {
        if (variant.options[n] !== chosen[n]) {
          same = false;
          break;
        }
      }
      if (same) {
        match = variant;
        break;
      }
    }

    var idField = this.form && this.form.querySelector('.product-variant-id');
    var submit = this.form && this.form.querySelector('[type="submit"]');
    var label = submit && submit.querySelector('span');

    if (!match) {
      // A combination that does not exist is not the same as one that is
      // out of stock, but both mean the same thing to the person: they
      // cannot have it.
      if (idField) idField.disabled = true;
      if (submit) submit.setAttribute('disabled', 'disabled');
      if (label) label.textContent = this.getAttribute('data-unavailable') || 'Unavailable';
      return;
    }

    if (idField) {
      idField.value = match.id;
      idField.disabled = false;
    }
    if (submit) {
      if (match.available) {
        submit.removeAttribute('disabled');
        if (label) label.textContent = this.getAttribute('data-add') || 'Add to basket';
      } else {
        submit.setAttribute('disabled', 'disabled');
        if (label) label.textContent = this.getAttribute('data-sold-out') || 'Sold out';
      }
    }

    this.paint('[data-tf-price]', match.price_html);
    this.paint('[data-tf-compare]', match.compare_html);
    this.paint('[data-tf-sku]', match.sku);

    // The address bar follows the choice, so a copied link opens on the
    // variant the person was looking at.
    if (match.url && window.history && window.history.replaceState) {
      window.history.replaceState({}, '', match.url);
    }

    this.dispatchEvent(
      new CustomEvent('tf:variant', { bubbles: true, detail: { variant: match } })
    );
  };

  TFVariants.prototype.paint = function (selector, value) {
    var targets = this.root.querySelectorAll(selector);
    Array.prototype.forEach.call(targets, function (node) {
      node.innerHTML = value || '';
      node.hidden = !value;
    });
  };

  /* ------------------------------------------------------------
     Product thumbnails
     ------------------------------------------------------------
     Swaps the main image. No carousel: the pictures are all on show and
     clicking one enlarges it, which is the whole interaction.
     ------------------------------------------------------------ */
  function bindThumbs(root) {
    var groups = (root || document).querySelectorAll('[data-tf-product]');

    Array.prototype.forEach.call(groups, function (group) {
      if (group.tfThumbsBound) return;
      group.tfThumbsBound = true;

      var main = group.querySelector('.tf-pmain__image');
      if (!main) return;

      group.addEventListener('click', function (event) {
        var thumb = event.target.closest('[data-tf-thumb]');
        if (!thumb) return;

        var src = thumb.getAttribute('data-tf-thumb');
        if (!src) return;

        main.src = src;
        // The srcset would otherwise keep winning over the new src.
        main.removeAttribute('srcset');

        var all = group.querySelectorAll('[data-tf-thumb]');
        Array.prototype.forEach.call(all, function (other) {
          other.setAttribute('aria-current', other === thumb ? 'true' : 'false');
        });
      });
    });
  }

  /* ------------------------------------------------------------
     <tf-sticky-buy> — the sticky add to cart
     ------------------------------------------------------------
     Appears once the real buy panel has scrolled away, and steps aside
     again when the footer arrives. Two rules, both about not being in
     the way: there is no point repeating a control that is already on
     screen, and a bar pinned over the footer covers the links someone
     scrolled all the way down to read.

     It does not hold a form of its own. Its button targets the panel's
     form by id, so the variant and the quantity are whatever the person
     chose up the page — one source of truth, and no syncing to get
     wrong. The cost is that Dawn drives the spinner on the button it
     finds inside the form, so the button's state is mirrored here
     instead of reimplemented.
     ------------------------------------------------------------ */
  function TFStickyBuy() {
    return Reflect.construct(HTMLElement, [], TFStickyBuy);
  }
  TFStickyBuy.prototype = Object.create(HTMLElement.prototype);
  TFStickyBuy.prototype.constructor = TFStickyBuy;
  Object.setPrototypeOf(TFStickyBuy, HTMLElement);

  TFStickyBuy.prototype.connectedCallback = function () {
    var el = this;

    this.anchorEl = document.querySelector(this.dataset.anchor || '');
    this.footerEl = document.querySelector(this.dataset.footer || '.tf-footer');
    this.button = this.querySelector('[data-tf-sticky-submit]');

    // Without something to hide behind, the bar would never go away.
    if (!this.anchorEl || !('IntersectionObserver' in window)) return;

    this.anchorVisible = true;
    this.footerVisible = false;

    this.watch(this.anchorEl, function (visible) {
      el.anchorVisible = visible;
      el.sync();
    });

    if (this.footerEl) {
      this.watch(this.footerEl, function (visible) {
        el.footerVisible = visible;
        el.sync();
      });
    }

    this.mirror();
  };

  TFStickyBuy.prototype.watch = function (target, onChange) {
    var observer = new IntersectionObserver(
      function (entries) {
        onChange(entries[0].isIntersecting);
      },
      // No margin, so the test is exactly the one asked for: the bar
      // appears when the real button has left the screen, not while a
      // sliver of it is still reachable.
      { threshold: 0 }
    );
    observer.observe(target);
  };

  TFStickyBuy.prototype.sync = function () {
    var show = !this.anchorVisible && !this.footerVisible;
    this.toggleAttribute('data-visible', show);
    // Out of the tab order while it is off screen, so a keyboard does
    // not land on a control nobody can see.
    this.setAttribute('aria-hidden', show ? 'false' : 'true');
    if (this.button) this.button.tabIndex = show ? 0 : -1;
  };

  TFStickyBuy.prototype.mirror = function () {
    var el = this;
    var source = document.querySelector(this.dataset.mirror || '');
    if (!source || !this.button || !('MutationObserver' in window)) return;

    function copy() {
      var busy = source.classList.contains('loading');
      el.button.classList.toggle('loading', busy);
      el.button.disabled = source.disabled;
      if (source.getAttribute('aria-disabled') === 'true') {
        el.button.setAttribute('aria-disabled', 'true');
      } else {
        el.button.removeAttribute('aria-disabled');
      }

      var label = source.querySelector('span');
      var target = el.button.querySelector('[data-tf-sticky-label]');
      if (label && target) target.textContent = label.textContent;
    }

    new MutationObserver(copy).observe(source, {
      attributes: true,
      attributeFilter: ['class', 'disabled', 'aria-disabled'],
      childList: true,
      subtree: true,
    });
    copy();
  };

  /* ------------------------------------------------------------
     <tf-carousel> — arrows and dots for a scroll-snap row
     The layout is pure CSS and switched per breakpoint there, so this
     element never decides WHETHER there is a carousel: it reads the
     track's computed overflow. When the row is a plain grid, or every
     card already fits, it sets nothing and the controls stay hidden.
     ------------------------------------------------------------ */
  class TFCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-tf-track]');
      if (!this.track) return;
      this.prevBtn = this.querySelector('[data-tf-prev]');
      this.nextBtn = this.querySelector('[data-tf-next]');
      this.dotsEl = this.querySelector('[data-tf-dots]');
      this.pages = 0;

      this.onScroll = () => {
        cancelAnimationFrame(this.raf);
        this.raf = requestAnimationFrame(() => this.update());
      };
      this.onPrev = () => this.go(-1);
      this.onNext = () => this.go(1);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);

      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.build());
        this.ro.observe(this.track);
      } else {
        this.onResize = () => this.build();
        window.addEventListener('resize', this.onResize);
      }
      this.build();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.onScroll);
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);
      if (this.ro) this.ro.disconnect();
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      cancelAnimationFrame(this.raf);
    }

    metrics() {
      var track = this.track;
      var first = track.firstElementChild;
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      var card = first ? first.getBoundingClientRect().width : track.clientWidth;
      var stride = card + gap;
      var perView = Math.max(1, Math.floor((track.clientWidth + gap) / stride + 0.01));
      return {
        stride: stride,
        perView: perView,
        max: Math.max(0, track.scrollWidth - track.clientWidth),
      };
    }

    isActive() {
      var overflow = getComputedStyle(this.track).overflowX;
      if (overflow !== 'auto' && overflow !== 'scroll') return false;
      return this.track.scrollWidth > this.track.clientWidth + 2;
    }

    build() {
      var active = this.isActive();
      this.toggleAttribute('data-active', active);
      if (!active) {
        this.pages = 0;
        if (this.dotsEl) this.dotsEl.innerHTML = '';
        return;
      }

      var m = this.metrics();
      var count = this.track.children.length;
      // One page per screenful: the first, plus as many full steps as it
      // takes to reach the end of the row (the last step may be short).
      var pageWidth = m.perView * m.stride;
      var pages = Math.min(count, 1 + Math.max(0, Math.ceil(m.max / pageWidth - 0.02)));

      if (this.dotsEl && pages !== this.pages) {
        this.dotsEl.innerHTML = '';
        for (var i = 0; i < pages; i += 1) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'tf-carousel__dot';
          dot.setAttribute('aria-label', 'Go to page ' + (i + 1) + ' of ' + pages);
          dot.dataset.page = String(i);
          dot.addEventListener('click', (event) => {
            this.goTo(parseInt(event.currentTarget.dataset.page, 10));
          });
          this.dotsEl.appendChild(dot);
        }
      }
      this.pages = pages;
      this.update();
    }

    scrollTo(left) {
      this.track.scrollTo({
        left: left,
        behavior: reduceMotion.matches ? 'auto' : 'smooth',
      });
    }

    go(direction) {
      var m = this.metrics();
      this.scrollTo(this.track.scrollLeft + direction * m.perView * m.stride);
    }

    goTo(page) {
      var m = this.metrics();
      this.scrollTo(Math.min(m.max, page * m.perView * m.stride));
    }

    update() {
      if (!this.hasAttribute('data-active')) return;
      var m = this.metrics();
      var x = this.track.scrollLeft;
      var atStart = x <= 2;
      var atEnd = x >= m.max - 2;

      if (this.prevBtn) this.prevBtn.disabled = atStart;
      if (this.nextBtn) this.nextBtn.disabled = atEnd;

      if (this.dotsEl && this.pages) {
        var current = atEnd
          ? this.pages - 1
          : Math.min(this.pages - 1, Math.round(x / (m.perView * m.stride)));
        Array.prototype.forEach.call(this.dotsEl.children, function (dot, i) {
          dot.setAttribute('aria-current', i === current ? 'true' : 'false');
        });
      }
    }
  }

  /* ------------------------------------------------------------
     <tf-preview> — the "Look inside" dialog
     ------------------------------------------------------------
     Wraps a native <dialog>. showModal() already gives focus trapping,
     Escape, and a page that cannot be reached behind it, so this only
     does the three things it cannot: open it from a button anywhere on
     the page, give the file to the iframe the first time it is asked
     for, and run the animation.

     The src is withheld until opening. A sample PDF runs to several
     megabytes, and loading it with the page charges every visitor for
     something most of them never open.
     ------------------------------------------------------------ */
  function TFPreview() {
    return Reflect.construct(HTMLElement, [], TFPreview);
  }
  TFPreview.prototype = Object.create(HTMLElement.prototype);
  TFPreview.prototype.constructor = TFPreview;
  Object.setPrototypeOf(TFPreview, HTMLElement);

  TFPreview.prototype.connectedCallback = function () {
    var el = this;
    this.dialog = this.querySelector('dialog');
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

    this.frame = this.querySelector('[data-tf-preview-src]');
    this.panel = this.querySelector('.tf-preview__panel');

    // Triggers live outside this element — over the image, under it —
    // so they are found by the dialog's id rather than by descent.
    var id = this.dialog.id;
    this.openers = document.querySelectorAll('[data-tf-preview-open="' + id + '"]');
    Array.prototype.forEach.call(this.openers, function (button) {
      button.addEventListener('click', function () {
        el.open(button);
      });
    });

    this.querySelectorAll('[data-tf-preview-close]').forEach(function (button) {
      button.addEventListener('click', function () {
        el.close();
      });
    });

    // The backdrop is the dialog itself; the panel sits inside it, so a
    // click that lands on the dialog and not the panel is a click
    // outside.
    this.dialog.addEventListener('click', function (event) {
      if (event.target === el.dialog) el.close();
    });

    this.dialog.addEventListener('close', function () {
      el.dialog.removeAttribute('data-open');
      lockScroll(false);
      if (el.opener && typeof el.opener.focus === 'function') {
        el.opener.focus();
      }
    });
  };

  TFPreview.prototype.open = function (opener) {
    var el = this;
    this.opener = opener;

    // Chrome on Android cannot render a PDF and gives an iframe pointed
    // at one a broken-document icon. It is honest about that, so ask
    // first and show the fallback panel rather than the broken box.
    // The property is undefined on older browsers, where the iframe was
    // working already — only an explicit false changes course.
    var noViewer = navigator.pdfViewerEnabled === false;
    if (noViewer) this.dialog.setAttribute('data-no-pdf', '');

    // First open only: after this the browser keeps the rendered PDF.
    if (!noViewer && this.frame && !this.frame.src) {
      this.frame.src = this.frame.getAttribute('data-tf-preview-src');
    }

    this.dialog.showModal();
    lockScroll(true);

    // One frame later, so the transition has a state to move from.
    requestAnimationFrame(function () {
      el.dialog.setAttribute('data-open', '');
    });
  };

  TFPreview.prototype.close = function () {
    var el = this;
    this.dialog.removeAttribute('data-open');

    if (reduceMotion.matches) {
      this.dialog.close();
      return;
    }

    // Let the sheet slide back down before the dialog is torn out of
    // the page, or it simply vanishes.
    var done = false;
    function finish() {
      if (done) return;
      done = true;
      el.dialog.close();
    }
    this.panel.addEventListener('transitionend', finish, { once: true });
    setTimeout(finish, 400);
  };

  /* ------------------------------------------------------------
     Registration & lifecycle
     ------------------------------------------------------------ */
  function define(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }

  define('tf-sticky-header', TFStickyHeader);
  define('tf-disclosure', TFDisclosure);
  define('tf-drawer', TFDrawer);
  define('tf-rotator', TFRotator);
  define('tf-video-facade', TFVideoFacade);
  define('tf-qty', TFQty);
  define('tf-variants', TFVariants);
  define('tf-sticky-buy', TFStickyBuy);
  define('tf-preview', TFPreview);
  define('tf-carousel', TFCarousel);

  function init(root) {
    reveal(root);
    bindForms(root);
    watchHeader();
    scopeFormSuccess();
    bindThumbs(root);
  }

  window.TF = {
    reveal: reveal,
    busy: busy,
    trapFocus: trapFocus,
    lockScroll: lockScroll,
    measureHeader: measureHeader,
    init: init,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Theme editor: re-initialise whatever the merchant just changed.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
  document.addEventListener('shopify:section:unload', function () {
    watchHeader();
  });
})();
